/**
 * The unread dot (#1069 P3.2, FR53/FR54/FR55).
 *
 * ⚠️ THIS COMPONENT EXISTS TO MAKE ONE ACCESSIBILITY RULE STRUCTURAL RATHER
 * THAN A REVIEW CHECKLIST: **the signal is the dot's PRESENCE, never its
 * colour.** It renders NOTHING when there is nothing unread — no wrapper, no
 * zero-opacity view, no spacer, no muted variant. There is no "read" dot.
 * Where a surface needs the column to stay put whether or not a dot is in it,
 * the SURFACE reserves the slot outside this component — the panel's thread
 * row wraps this in a `View` of `UNREAD_DOT_SIZE` (Story 5.4, D6) — so the
 * rule here never bends to a layout need.
 *
 * A dot that is always rendered and merely changes hue (grey to red, muted to
 * primary) FAILS WCAG 1.4.1 outright, because colour becomes the only carrier
 * of the state. It is stated in these words because it is the implementation
 * everyone reaches for first, and because a colour-only version would pass a
 * screenshot review.
 *
 * ⚠️ THIS IS STILL THE SIGNAL ALMOST EVERYWHERE (#1069 P3.3). The thread
 * card, the request-list row and the launcher pill all render this dot; only
 * the panel's space TABS carry the "New" badge instead, because they are the
 * one surface with room for a word. See `UnreadBadge.tsx`.
 *
 * ⚠️ ONE APPEARANCE WHEREVER IT RENDERS. NO `tone` PROP, NO PER-GROUND BRANCH.
 * The 2px `colors.backgroundPrimary` ring is LOAD-BEARING, not decoration: it
 * makes the dot's immediate surround white WHATEVER sits underneath, so the
 * fill-against-ring contrast is 3.55:1 on a table cell, a card, a thread row
 * and the orange launcher pill alike. That matters because the pill's ground is
 * not even constant — `styles.toggleOn` swaps it to `colors.primaryDark` while
 * the panel is open. Picking a colour per surface was tried in an earlier
 * revision and could not be made to work; the ring discharges SC 1.4.11 by
 * construction.
 *
 * Measured against `src/theme/colors.ts`: `colors.primary` on white is 2.84:1
 * (fails), `#fff` on the pill is 2.84:1 (fails), `colors.error` on white is
 * 3.55:1 (passes). Red is also the platform convention for a notification dot
 * on iOS and Android, so it arrives already learned — the owner's explicit call
 * on 2026-08-25.
 *
 * ⚠️ `colors.error` IS ALSO THE APP'S ERROR HUE, AND UNREAD IS NOT AN ERROR.
 * The dot must never read as one, which is exactly why its PRESENCE is the
 * whole signal and why it is never accompanied by error copy, an alert role or
 * a live region. If a future reviewer asks why an error token marks a non-error
 * state, the answer is convention, not severity.
 *
 * ⚠️ NO LABEL OF ITS OWN, AND THAT IS DELIBERATE. A role-less `View` carrying
 * an `accessibilityLabel` is not focusable by VoiceOver, and on web
 * react-native-web emits `aria-label` on a plain `div`, which screen readers
 * ignore. A label here would be one that is never spoken — worse than none,
 * because it makes the suite look covered. Each level attaches its own spoken
 * state; see `VisuallyHiddenText` and the launcher's composed label.
 *
 * ⚠️ NO ANIMATION, NO PULSE, NO TRANSITION, NO `Animated` IMPORT. A pulsing
 * badge on a product whose PRD makes ASYNCHRONICITY a requirement is a lie
 * about urgency; it would also manufacture a reduced-motion obligation out of
 * nothing and has no natural end state, because nothing here polls.
 *
 * @see GitLab #1069 — Request Correspondence
 */

import React from 'react';
import { StyleSheet, View } from 'react-native';

import { colors } from '../../theme/colors';

import type { StyleProp, ViewStyle } from 'react-native';

/**
 * 8px of fill inside a 2px ring: 12px overall, the standard badge build.
 *
 * Was module-private from Story 2.4 (the panel's corner-dot reserve went away
 * with the card). The reason it is exported CHANGED on 2026-09-23 and the old
 * one is recorded here because it was load-bearing: `ViewRequests` used to
 * reserve in-flow room for this dot in the request-list actions column, and was
 * doing it by hand-copying the literal `12` (code review P8). The row marker's
 * dot now overhangs the glyph's corner and is out of flow, so the list reserves
 * nothing and that consumer is gone.
 *
 * The remaining consumer is `CorrespondencePanel`'s thread row, which wraps this
 * in a `View` of `UNREAD_DOT_SIZE` so the column stays put read or unread
 * (Story 5.4, D6). Import it; do not retype it.
 */
const DOT_SIZE = 8;
const RING = 2;
export const UNREAD_DOT_SIZE = DOT_SIZE + RING * 2;

interface Props {
  /**
   * `undefined` means the server's read-state lookup failed for this listing
   * (since Story 2.1 that is its only meaning: an oversight reader's unread is
   * computed like everybody else's, FR52), and `false` means it computed and
   * found nothing. Both render as nothing, which is why the distinction is kept
   * in the types rather than here.
   */
  unread?: boolean;
  style?: StyleProp<ViewStyle>;
  testID?: string;
}

export const UnreadDot: React.FC<Props> = ({
  unread,
  style,
  testID = 'unread-dot',
}) =>
  unread ? (
    <View
      style={[styles.dot, style]}
      testID={testID}
      /**
       * ⚠️ IT MUST NEVER EAT A TAP. On the launcher the dot overhangs the
       * pill's top-right corner, and the wrapper around them is `box-none` —
       * which stops the WRAPPER capturing but not its children. Without this a
       * tap on those 12px hits the dot and does nothing, which reads as a dead
       * button exactly where the eye was drawn. It is a status, never a target,
       * at all three levels.
       */
      pointerEvents="none"
      // Never announced on its own: a dot says nothing, and a separately
      // announced one would be noise even if a `View` label were spoken.
      accessibilityElementsHidden
      importantForAccessibility="no-hide-descendants"
    />
  ) : null;

const styles = StyleSheet.create({
  dot: {
    width: UNREAD_DOT_SIZE,
    height: UNREAD_DOT_SIZE,
    borderRadius: UNREAD_DOT_SIZE / 2,
    backgroundColor: colors.error,
    borderWidth: RING,
    borderColor: colors.backgroundPrimary,
  },
});
